// =======================================================================================================
// React
import * as React from "react";
import { SketchPicker } from 'react-color';
import _ from "lodash";

// Material UI components
import { List, ListItem } from 'material-ui/List';
import Slider from 'material-ui/Slider';
import Toggle from 'material-ui/Toggle';
import Drawer from 'material-ui/Drawer';
import TextField from 'material-ui/TextField';
import FlatButton from 'material-ui/FlatButton';
import Popover from 'material-ui/Popover/Popover';
import DropDownMenu from 'material-ui/DropDownMenu';
import MenuItem from 'material-ui/MenuItem';
import IconButton from 'material-ui/IconButton';

// Icons
import BackgroundImageUploadIcon from 'material-ui/svg-icons/file/file-upload';
import CleanBackgroundIcon from 'material-ui/svg-icons/content/clear';

// Internal components
import { getIconClassName, IconInfo } from "@Primitive/IconSelectorUtils";
import { LazyLoadingAnimation } from "@Primitive/Lazy";
import { DashboardConfiguration, PageLayoutPolicy, PollingScale } from '@ModelViews/ModelViews';
import { Checkbox } from 'material-ui';
import { IDebugProp } from '@Primitive/Interfaces';
import { DashboardHelper } from './DashboardStructs';
import { isEqualsSafe } from '@Utils/Utils';
import { DomUtils } from '@Utils/Dom';
import { IsNullOrEmpty, isStringEmpty } from '@Utils/Micro';
import { ExDialog } from '@Primitive/ExDialog';
import { BoardTheming } from "./BoardTheming";

const IconSelector = React.lazy(() => import("@Primitive/IconSelector"));

// =======================================================================================================
export interface DashboardConfigurationViewProps extends React.Props<DashboardConfigurationView>, IDebugProp {
    configurationKey: string;
    configuration: DashboardConfiguration;

    onConfigurationChange?: (key: string, newConf: DashboardConfiguration) => void;
    onConfigurationClose?: (key: string) => void;

    // Drawer options
    isDrawer: boolean;
    isOpen?: boolean;
    drawerWidth?: number;

    inEditMode: boolean;

    // othe pages for comparing
    otherPages: DashboardConfiguration[];
}

export interface DashboardConfigurationViewState {
    configuration: DashboardConfiguration;
    openWidgetBgColorPicker: boolean;
    openCanvasBgColorPicker: boolean;
    openOutlineColorPicker: boolean;

    // background image variables
    errorMessage: string;
    processingImage: boolean;
    imageNeedsResize: boolean;
    mobileImageNeedsResize: boolean;

    previewIcon: any;
    openIconSelector: boolean;

    errorMessagePageId: string;
}
// ------------------------------------------------------------------------------------------------------
export class DashboardConfigurationView extends React.Component<DashboardConfigurationViewProps, DashboardConfigurationViewState> {
    // ------------------------------------------------------------------------------------------------------
    private static MaxImageWidth: number = 1920 * 1.5;
    private static MaxImageHeight: number = 1080 * 1.5;
    public anchorEl: any;
    public canvasBgColorEl: HTMLDivElement;
    public outlineColorEl: HTMLDivElement;
    // ------------------------------------------------------------------------------------------------------
    constructor(props) {
        super(props);
        this.state = {
            configuration: _.merge({},
                DashboardHelper.getDefaultConfiguration(),
                this.props.configuration),
            openWidgetBgColorPicker: false,
            openCanvasBgColorPicker: false,
            openOutlineColorPicker: false,
            errorMessage: '',
            processingImage: false,
            imageNeedsResize: false,
            mobileImageNeedsResize: false,
            previewIcon: null,
            openIconSelector: false,
            errorMessagePageId: null
        };
    }
    // ------------------------------------------------------------------------------------------------------
    private onConfigurationClose = () => {
        const onConfigurationChange = (changed: boolean = false) => {
            if (changed && this.props.onConfigurationChange)
                this.props.onConfigurationChange(this.props.configurationKey, this.state.configuration);

            if (this.props.onConfigurationClose)
                this.props.onConfigurationClose(this.props.configurationKey);
        }
        // need to check if image is valid
        if (!isEqualsSafe(this.props.configuration, this.state.configuration)) {

            if (this.state.imageNeedsResize || this.state.mobileImageNeedsResize) {
                if (this.state.imageNeedsResize) {
                    DomUtils
                        .getResizedImage(
                            this.state.configuration.BackgroundImage,
                            DashboardConfigurationView.MaxImageWidth,
                            DashboardConfigurationView.MaxImageHeight, 'jpeg')
                        .then(result => {
                            this.setState((state: DashboardConfigurationViewState) => {
                                // update source of desktop background image
                                state.configuration.BackgroundImage = result.src;
                                return state;
                            }, () => {
                                onConfigurationChange.call(this, true);
                            });
                        })
                        .catch(() => {
                            this.setState((state: DashboardConfigurationViewState) => {
                                // resizing error
                                state.errorMessage = 'Invalid URL for desktop background image';
                                state.processingImage = false;
                                state.imageNeedsResize = true;
                                return state;
                            });
                        });
                }
                if (this.state.mobileImageNeedsResize) {
                    DomUtils
                        .getResizedImage(
                            this.state.configuration.MobileBackgroundImage,
                            DashboardConfigurationView.MaxImageWidth,
                            DashboardConfigurationView.MaxImageHeight, 'jpeg')
                        .then(result => {
                            this.setState((state: DashboardConfigurationViewState) => {
                                // update source of mobile background image
                                state.configuration.MobileBackgroundImage = result.src;
                                return state;
                            }, () => {
                                onConfigurationChange.call(this, true);
                            });
                        })
                        .catch(() => {
                            this.setState((state: DashboardConfigurationViewState) => {
                                // resizing error
                                state.errorMessage = 'Invalid URL for mobile background image';
                                state.processingImage = false;
                                state.mobileImageNeedsResize = true;
                                return state;
                            });
                        });
                }
            } else {
                onConfigurationChange.call(this, true);
            }
        } else {
            onConfigurationChange.call(this);
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private onConfigurationCloseWithoutSave = () => {
        if (this.props.onConfigurationClose)
            this.props.onConfigurationClose(this.props.configurationKey);
    }
    // ------------------------------------------------------------------------------------------------------
    private onToggle = () => {
        this.setState(s => { s.configuration.VerticalCompact = !s.configuration.VerticalCompact; return s; });
    }
    // ------------------------------------------------------------------------------------------------------
    private onMarginHChange = (e: React.MouseEvent<{}>, value: number) => {
        this.setState(s => { s.configuration.Margin.H = value; return s; });
    }
    // ------------------------------------------------------------------------------------------------------
    private onMarginWChange = (e: React.MouseEvent<{}>, value: number) => {
        this.setState(s => { s.configuration.Margin.W = value; return s; });
    }
    // ------------------------------------------------------------------------------------------------------
    private onZDepthChange = (e: React.MouseEvent<{}>, value: number) => {
        this.setState(s => { s.configuration.ZDepth = value; return s; });
    }
    // ------------------------------------------------------------------------------------------------------
    private onWidthListThresholdChange = (e: React.MouseEvent<{}>, value: number) => {
        this.setState(s => { s.configuration.WidthListThreshold = value; return s; });
    }
    // ------------------------------------------------------------------------------------------------------
    private onBorderRadiusChange = (e: React.MouseEvent<{}>, value: number) => {
        this.setState(s => { s.configuration.BorderRadius = value; return s; });
    }
    // ------------------------------------------------------------------------------------------------------
    private onMaxPageWidthChange = (e: React.MouseEvent<{}>, value: number) => {
        this.setState(s => { s.configuration.MaxPageWidth = value; return s; });
    }
    // ------------------------------------------------------------------------------------------------------
    private onPaddingChange = (e: React.MouseEvent<{}>, value: number) => {
        this.setState(s => { s.configuration.Padding = value; return s; });
    }
    // ------------------------------------------------------------------------------------------------------
    private onOutlineThicknessChange = (e: React.MouseEvent<{}>, value: number) => {
        this.setState(s => { s.configuration.Outline.Thickness = value; return s; });
    }
    // ------------------------------------------------------------------------------------------------------
    private updateBackgroundImageSource(value: string, fromFileUpload: boolean = false, forMobile: boolean = false) {
        // reset status
        if (IsNullOrEmpty(value)) {
            this.setState((state: DashboardConfigurationViewState) => {
                if (forMobile) {
                    state.configuration.MobileBackgroundImage = '';
                    state.mobileImageNeedsResize = false;
                } else {
                    state.configuration.BackgroundImage = '';
                    state.imageNeedsResize = false;
                }
                state.errorMessage = '';
                state.processingImage = false;
                return state;
            });
            return;
        }
        if (fromFileUpload) {
            // we are processing an image
            this.setState((state: DashboardConfigurationViewState) => {
                state.processingImage = true;
                return state;
            });

            DomUtils
                .getResizedImage(value,
                    DashboardConfigurationView.MaxImageWidth,
                    DashboardConfigurationView.MaxImageHeight, 'jpeg')
                .then(result => {
                    this.setState((state: DashboardConfigurationViewState) => {
                        // update source of image
                        if (forMobile) {
                            state.configuration.MobileBackgroundImage = result.src;
                            state.mobileImageNeedsResize = false;
                        } else {
                            state.configuration.BackgroundImage = result.src;
                            state.imageNeedsResize = false;
                        }
                        state.processingImage = false;
                        return state;
                    });
                })
                .catch(er => {
                    // resizing error
                    this.setState((state: DashboardConfigurationViewState) => {
                        state.errorMessage = (er as Error).message;
                        state.processingImage = false;

                        if (forMobile) {
                            state.mobileImageNeedsResize = false;
                        } else {
                            state.imageNeedsResize = false;
                        }
                        return state;
                    });
                });
        }
        else {
            this.setState((state: DashboardConfigurationViewState) => {
                // resize image after update
                if (forMobile) {
                    state.configuration.MobileBackgroundImage = value;
                    state.mobileImageNeedsResize = true;
                } else {
                    state.configuration.BackgroundImage = value;
                    state.imageNeedsResize = true;
                }
                return state;
            });
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private renderBody() {
        const theme = BoardTheming.GetActiveTheme();
        const fieldStyle: React.CSSProperties = {
            color: theme.dialog.fontColor ?? 'rgba(0, 0, 0, 0.87)',
            width: 'calc(100% - 36px)',
            marginLeft: 18,
            fontSize: 'inherit'
        };
        const colorStyle: React.CSSProperties = {
            color: theme.dialog.fontColor ?? 'rgba(0, 0, 0, 0.87)',
            fontWeight: 'bold'
        };

        const fieldLabelStyle: React.CSSProperties = {
            color: theme.dialog.fontColor ?? 'rgba(0, 0, 0, 0.87)',
            fontSize: 20
        };

        const conf = this.state.configuration;
        const outline = conf.Outline;

        const emptyImage = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';
        const fileInputStyle: React.CSSProperties = {
            width: '100%',
            display: 'none',
            position: 'absolute',
            cursor: 'pointer',
            opacity: 0,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
        };
        return (
            <List style={{ padding: 0 }}>
                <TextField
                    key={"Name"}
                    fullWidth={true}
                    floatingLabelText="Name"
                    floatingLabelFixed={true}
                    floatingLabelStyle={fieldLabelStyle}
                    style={fieldStyle}
                    inputStyle={colorStyle}
                    value={conf.Name}
                    onChange={(e: React.FormEvent<{}>, newValue: string) => {
                        this.setState(s => { s.configuration.Name = newValue; return s; })
                    }} />

                <TextField
                    key={"Description"}
                    fullWidth={true}
                    floatingLabelText="Description"
                    floatingLabelFixed={true}
                    floatingLabelStyle={fieldLabelStyle}
                    style={fieldStyle}
                    inputStyle={colorStyle}
                    value={conf.Desc}
                    onChange={(e: React.FormEvent<{}>, newValue: string) => {
                        this.setState(s => { s.configuration.Desc = newValue; return s; })
                    }} />

                <TextField
                    key={"PageId"}
                    fullWidth={true}
                    floatingLabelText="Page ID"
                    floatingLabelFixed={true}
                    floatingLabelStyle={fieldLabelStyle}
                    style={fieldStyle}
                    inputStyle={colorStyle}
                    value={conf.PageId}
                    errorText={this.state.errorMessagePageId}
                    onChange={(e: React.FormEvent<{}>, newValue: string) => {
                        this.setState((s: DashboardConfigurationViewState) => {
                            if ((_.filter(this.props.otherPages, x => x.PageId == newValue).length > 0) &&
                                (newValue != this.props.configuration.PageId))
                                s.errorMessagePageId = "Duplicate Page ID";
                            else
                                s.errorMessagePageId = null;

                            s.configuration.PageId = newValue;
                            return s;
                        })
                    }} />

                <TextField
                    key={"Key"}
                    fullWidth={true}
                    disabled={true}
                    defaultValue={this.props.configurationKey}
                    floatingLabelText="Key"
                    floatingLabelStyle={fieldLabelStyle}
                    inputStyle={colorStyle}
                    style={fieldStyle} />

                <ListItem
                    key='layout'
                    primaryText="Layout"
                    primaryTogglesNestedList={true}
                    style={colorStyle}
                    nestedItems={[
                        // ------------------------------------
                        <ListItem
                            key='compact-layout'
                            primaryText="Compact layout"
                            secondaryText='Widgets are closely and neatly packed together'
                            style={colorStyle}
                            rightToggle={
                                <Toggle toggled={conf.VerticalCompact} style={colorStyle} onToggle={this.onToggle} />} />,
                        // ------------------------------------
                        <ListItem
                            key='grid-layout'
                            primaryText="Grid layout"
                            secondaryText='A framework of spaced bars that are parallel'
                            style={colorStyle}
                            rightToggle={
                                <Toggle
                                    toggled={conf.GridLayout}
                                    onToggle={(e: React.MouseEvent<{}>, isInputChecked: boolean) => {
                                        this.setState(s => { s.configuration.GridLayout = isInputChecked; return s; });
                                    }} />} />,
                        // ------------------------------------
                        <ListItem
                            key='vertical-grid'
                            primaryText={"Vertical grid size [" + conf.NumberOfVerticalGridSpaces + "]"}
                            secondaryText='The number of columns displayed in the grid'
                            style={colorStyle}
                            primaryTogglesNestedList={true}
                            nestedItems={[
                                <Slider
                                    key={"layout-vertical-space"}
                                    style={{ margin: '0 16px 0 34px' }}
                                    sliderStyle={{ margin: 0 }}
                                    value={conf.NumberOfVerticalGridSpaces}
                                    min={6}
                                    max={36}
                                    step={6}
                                    onChange={(e: React.MouseEvent<{}>, value: number) =>
                                        this.setState(s => { s.configuration.NumberOfVerticalGridSpaces = value; return s; })} />]} />,
                        // ------------------------------------
                        <ListItem
                            key='max-page-width'
                            primaryText={"Max page width  [" + conf.MaxPageWidth + "]"}
                            secondaryText='The maximum width of canvas'
                            style={colorStyle}
                            primaryTogglesNestedList={true}
                            nestedItems={[
                                <Slider
                                    key={"zDepth-slider"}
                                    style={{ margin: '0 16px 0 34px' }}
                                    sliderStyle={{ margin: 0 }}
                                    value={conf.MaxPageWidth}
                                    min={500}
                                    max={2600}
                                    step={50}
                                    onChange={this.onMaxPageWidthChange}
                                />
                            ]} />,
                        // ------------------------------------
                        <ListItem
                            key='margin'
                            primaryText={"Margin  [" + conf.Margin.W + "," + conf.Margin.H + "]"}
                            secondaryText={"Space between the widgets. For no space set it to zero."}
                            style={colorStyle}
                            primaryTogglesNestedList={true}
                            nestedItems={[
                                <p key={"Horizontal"} style={{ margin: '4px 16px 4px 34px' }}>
                                    <span>{"Horizontal"}</span>
                                </p>,
                                <Slider
                                    key={"HorizontalSlider"}
                                    style={{ margin: '0 16px 0 34px' }}
                                    sliderStyle={{ margin: 0 }}
                                    value={conf.Margin.W}
                                    min={0}
                                    max={20}
                                    step={1}
                                    onChange={this.onMarginWChange} />,

                                <p key={"Vertical"} style={{ margin: '0 16px 0 34px' }}>
                                    <span>{"Vertical"}</span>
                                </p>,
                                <Slider
                                    key={"VerticalSlider"}
                                    style={{ margin: '0 16px 0 34px' }}
                                    sliderStyle={{ margin: 0 }}
                                    value={conf.Margin.H}
                                    min={0}
                                    max={20}
                                    step={1}
                                    onChange={this.onMarginHChange} />
                            ]}
                        />,
                        // ------------------------------------
                        <ListItem
                            key='padding'
                            primaryText={"Padding  [" + conf.Padding + "]"}
                            secondaryText='The space around canvas'
                            style={colorStyle}
                            primaryTogglesNestedList={true}
                            nestedItems={[
                                <Slider
                                    key={"padding-slider"}
                                    style={{ margin: '0 16px 0 34px' }}
                                    sliderStyle={{ margin: 0 }}
                                    value={conf.Padding}
                                    min={0}
                                    max={20}
                                    step={1}
                                    onChange={this.onPaddingChange}
                                />
                            ]} />,
                        // ------------------------------------
                        <ListItem
                            key={'is-dashboard-page-hidden'}
                            primaryText={'Visible'}
                            secondaryText={"Hides the page from the list"}
                            style={colorStyle}
                            rightToggle={
                                <Toggle
                                    disabled={this.props.inEditMode}
                                    toggled={!conf.IsHidden}
                                    onToggle={(e: React.MouseEvent<{}>, isInputChecked: boolean) => {
                                        this.setState(s => {
                                            s.configuration.IsHidden = !isInputChecked;
                                            return s;
                                        });
                                    }} />} />,
                        // ------------------------------------
                        <ListItem
                            key={'page-layout'}
                            primaryText={'Page layout policy'}
                            style={colorStyle}
                            rightIconButton={
                                <DropDownMenu
                                    value={conf.LayoutPolicy}
                                    onChange={(e, i, v) => {
                                        this.setState(s => {
                                            s.configuration.LayoutPolicy = v;
                                            return s;
                                        });
                                    }} >
                                    <MenuItem key={PageLayoutPolicy.FillWidth} value={PageLayoutPolicy.FillWidth} primaryText="Fill width" />
                                    <MenuItem key={PageLayoutPolicy.FillHeight} value={PageLayoutPolicy.FillHeight} primaryText="Fill height" />
                                    {/* <MenuItem key={Yodiwo.ModelViews.DashboardStructs.PageLayoutPolicy.AvoidScrollbar} value={Yodiwo.ModelViews.DashboardStructs.PageLayoutPolicy.AvoidScrollbar} primaryText="Avoid Scroll" /> */}
                                </DropDownMenu>} />,

                        // ------------------------------------
                        <ListItem
                            key='WidthListThreshold'
                            primaryText={"Width Mobile Threshold [" + conf.WidthListThreshold + "]"}
                            style={colorStyle}
                            primaryTogglesNestedList={true}
                            leftCheckbox={
                                <Checkbox
                                    checked={conf.WidthListThreshold != null}
                                    onCheck={(event, checked) => {
                                        this.setState((s: DashboardConfigurationViewState) => {
                                            if (checked)
                                                s.configuration.WidthListThreshold = 800
                                            else
                                                s.configuration.WidthListThreshold = undefined;
                                            return s;
                                        });
                                    }}
                                />}
                            nestedItems={
                                (conf.WidthListThreshold != null) ? [
                                    <Slider
                                        key={"WidthListThreshold-slider"}
                                        style={{ margin: '0 16px 0 34px' }}
                                        sliderStyle={{ margin: 0 }}
                                        value={conf.WidthListThreshold}
                                        min={0}
                                        max={1600}
                                        step={10}
                                        onChange={this.onWidthListThresholdChange}
                                    />] : undefined
                            } />,
                    ]} />
                <ListItem
                    key='theme'
                    primaryText="Theme"
                    primaryTogglesNestedList={true}
                    style={colorStyle}
                    nestedItems={[
                        // ------------------------------------
                        <ListItem
                            primaryText={"Page icon"}
                            key={"Page-Icon"}
                            style={colorStyle}
                            rightIconButton={<IconButton
                                key={"Settings-Icon"}
                                style={{
                                    backgroundColor: "lightgray",
                                    borderRadius: "50%",
                                    width: 34,
                                    height: 34,
                                    top: 8,
                                    right: 12
                                }}
                                iconStyle={{ fontSize: 14 }}
                                iconClassName={getIconClassName(conf.Icon)}
                                touch={true}
                                onClick={() => this.setState({ openIconSelector: true })} >
                            </IconButton>}
                        />,
                        <ExDialog
                            key='icon-selector'
                            Enable={this.state.openIconSelector}
                            Conf={{
                                title: 'Icon Selector',
                                contentStaticSize: { w: 0.5, h: 0.9 },
                                bodyFunc: (w, h) =>
                                    <React.Suspense fallback={<LazyLoadingAnimation />}>
                                        <IconSelector
                                            width={w}
                                            height={h}
                                            defaultIcon={{ className: this.state.configuration.Icon }}
                                            onIconSelection={(iconInfo: IconInfo) =>
                                                this.setState({ previewIcon: iconInfo.className })} />
                                    </React.Suspense>,
                                actions: [
                                    <IconButton tooltip="Cancel" iconClassName="fas fa-times-circle" touch={true} iconStyle={{ color: "white" }}
                                        onClick={() =>
                                            // Close icon selector.
                                            this.setState({
                                                openIconSelector: false,
                                                previewIcon: null
                                            })}
                                    />,
                                    <IconButton tooltip="Save" tooltipPosition='bottom-left' iconClassName="fas fa-upload" touch={true} iconStyle={{ color: "white" }}
                                        onClick={() => {
                                            this.setState((s: DashboardConfigurationViewState) => {
                                                // Get tab's icon.
                                                let icon: string = s.configuration.Icon;
                                                // Is there any change?
                                                if (!_.isEqual(s.previewIcon, icon)) {
                                                    s.configuration.Icon = s.previewIcon;
                                                }
                                                s.previewIcon = null;
                                                s.openIconSelector = false;
                                                return s;
                                            });
                                        }}
                                    />
                                ]
                            }}
                        />,
                        // ------------------------------------
                        <ListItem
                            key='background-image'
                            primaryTogglesNestedList
                            primaryText={"Desktop background image"}
                            initiallyOpen={false}
                            style={colorStyle}
                            nestedItems={[
                                <img
                                    key='img-tag'
                                    style={{
                                        objectFit: 'contain',
                                        maxHeight: 54,
                                        marginLeft: 34
                                    }}
                                    src={isStringEmpty(conf.BackgroundImage)
                                        ? emptyImage
                                        : conf.BackgroundImage} />,
                                <FlatButton
                                    key={"BackgroundImageUpload"}
                                    style={{
                                        float: "right",
                                        marginRight: 16
                                    }}
                                    containerElement="label"
                                    icon={<BackgroundImageUploadIcon />}
                                    disabled={this.state.processingImage}>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        style={fileInputStyle}
                                        onChange={(e) => {
                                            var input = e.target as HTMLInputElement;
                                            var files = input.files;
                                            if (files && files.length > 0) {
                                                var FR = new FileReader();
                                                FR.onload = (eFR: ProgressEvent) => {
                                                    this.updateBackgroundImageSource(FR.result as string, true);
                                                };
                                                FR.onerror = (eFR: ProgressEvent) => {
                                                    this.setState((s: DashboardConfigurationViewState) => {
                                                        s.configuration.BackgroundImage = "";
                                                        s.errorMessage = FR.error.message;
                                                        s.processingImage = false;
                                                        s.imageNeedsResize = false;
                                                        return s;
                                                    });
                                                };
                                                FR.readAsDataURL(files.item(0));
                                            }
                                        }} />
                                </FlatButton>,
                                <FlatButton
                                    key={"CleanBackgroundImage"}
                                    style={{ float: "right" }}
                                    containerElement="label"
                                    icon={<CleanBackgroundIcon />}
                                    disabled={isStringEmpty(conf.BackgroundImage)}
                                    onClick={() => {
                                        this.setState((s: DashboardConfigurationViewState) => {
                                            s.configuration.BackgroundImage = "";
                                            s.errorMessage = "";
                                            s.processingImage = false;
                                            s.imageNeedsResize = false;
                                            return s;
                                        });
                                    }}
                                />
                            ]} />,
                        // ------------------------------------
                        <ListItem
                            primaryTogglesNestedList
                            initiallyOpen={false}
                            key='mobile-background-image'
                            primaryText='Mobile background image'
                            style={colorStyle}
                            nestedItems={[
                                <img
                                    key='mobile-img-tag'
                                    style={{
                                        objectFit: 'contain',
                                        maxHeight: 54,
                                        marginLeft: 34
                                    }}
                                    src={isStringEmpty(conf.MobileBackgroundImage)
                                        ? emptyImage
                                        : conf.MobileBackgroundImage} />,
                                <FlatButton
                                    key='mobile-background-image-upload'
                                    style={{
                                        float: 'right',
                                        marginRight: 16
                                    }}
                                    containerElement='label'
                                    icon={<BackgroundImageUploadIcon />}
                                    disabled={this.state.processingImage}>
                                    <input
                                        type='file'
                                        accept='image/*'
                                        style={fileInputStyle}
                                        onChange={event => {
                                            let files = (event.target as HTMLInputElement).files;
                                            if (files?.length > 0) {
                                                let fileReader = new FileReader();
                                                fileReader.onload = () => {
                                                    // load image for mobile view
                                                    this.updateBackgroundImageSource(fileReader.result as string, true, true);
                                                };
                                                fileReader.onerror = () => {
                                                    this.setState((state: DashboardConfigurationViewState) => {
                                                        // error - reset uploading status
                                                        state.configuration.MobileBackgroundImage = '';
                                                        state.errorMessage = fileReader.error.message;
                                                        state.processingImage = false;
                                                        state.mobileImageNeedsResize = false;
                                                        return state;
                                                    });
                                                };
                                                fileReader.readAsDataURL(files.item(0));
                                            }
                                        }} />
                                </FlatButton>,
                                <FlatButton
                                    key='mobile-background-image-clean'
                                    style={{ float: 'right' }}
                                    containerElement='label'
                                    icon={<CleanBackgroundIcon />}
                                    disabled={isStringEmpty(conf.MobileBackgroundImage)}
                                    onClick={() => {
                                        this.setState((state: DashboardConfigurationViewState) => {
                                            // remove image for mobile view
                                            state.configuration.MobileBackgroundImage = '';
                                            state.errorMessage = '';
                                            state.processingImage = false;
                                            state.mobileImageNeedsResize = false;
                                            return state;
                                        });
                                    }} />
                            ]} />,
                        // ------------------------------------
                        <ListItem
                            key='canvas-background-color'
                            primaryText='Canvas background color'
                            style={colorStyle}
                            leftCheckbox={
                                <Checkbox
                                    checked={conf.OverrideCanvasBackgroundColor}
                                    onCheck={(event, checked) => {
                                        this.setState((s: DashboardConfigurationViewState) => {
                                            s.configuration.OverrideCanvasBackgroundColor = checked;
                                            return s;
                                        });
                                    }}
                                />
                            }
                            rightToggle={
                                (conf.OverrideCanvasBackgroundColor) ?
                                    <div>
                                        <div
                                            style={{
                                                padding: '5px',
                                                background: '#fff',
                                                borderRadius: '1px',
                                                boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                                                display: 'inline-block',
                                                cursor: 'pointer',
                                            }}
                                            ref={(element: HTMLDivElement) => this.canvasBgColorEl = element}
                                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                this.setState((s: DashboardConfigurationViewState) => {
                                                    s.openCanvasBgColorPicker = !s.openCanvasBgColorPicker;
                                                    return s;
                                                });
                                            }} >
                                            <div
                                                style={{
                                                    width: '36px',
                                                    height: '14px',
                                                    borderRadius: '2px',
                                                    background: conf.CanvasBackgroundColor
                                                }} />
                                        </div>
                                        <Popover
                                            open={this.state.openCanvasBgColorPicker}
                                            anchorEl={this.canvasBgColorEl}
                                            anchorOrigin={{
                                                'horizontal': 'left',
                                                'vertical': 'bottom'
                                            }}
                                            targetOrigin={{
                                                'horizontal': 'left',
                                                'vertical': 'top'
                                            }}
                                            onRequestClose={() => this.setState((s: DashboardConfigurationViewState) => {
                                                s.openCanvasBgColorPicker = false;
                                                return s;
                                            })} >
                                            <SketchPicker
                                                color={conf.CanvasBackgroundColor}
                                                onChange={(color) => {
                                                    this.setState((s: DashboardConfigurationViewState) => {
                                                        s.configuration.CanvasBackgroundColor =
                                                            'rgba(' + color.rgb.r + ',' + color.rgb.g + ',' + color.rgb.b + ',' + color.rgb.a + ')';
                                                        return s;
                                                    });
                                                }} />
                                        </Popover>
                                    </div> : null} />,
                        // ------------------------------------
                        <ListItem
                            key='widget-background-color'
                            primaryText="Widgets background color"
                            style={colorStyle}
                            leftCheckbox={
                                <Checkbox
                                    checked={conf.OverrideWidgetBackgroundColor}
                                    onCheck={(event, checked) => {
                                        this.setState((s: DashboardConfigurationViewState) => {
                                            s.configuration.OverrideWidgetBackgroundColor = checked;
                                            return s;
                                        });
                                    }}
                                />
                            }
                            rightToggle={
                                (conf.OverrideWidgetBackgroundColor) ?
                                    <div>
                                        <div
                                            style={{
                                                padding: '5px',
                                                background: '#fff',
                                                borderRadius: '1px',
                                                boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                                                display: 'inline-block',
                                                cursor: 'pointer',
                                            }}
                                            ref={(r) => this.anchorEl = r}
                                            onClick={(event) => { this.setState((s: DashboardConfigurationViewState) => { s.openWidgetBgColorPicker = !s.openWidgetBgColorPicker; return s; }) }}>
                                            <div style={{ width: '36px', height: '14px', borderRadius: '2px', background: conf.WidgetBackgroundColor }} />
                                        </div>
                                        <Popover
                                            open={this.state.openWidgetBgColorPicker}
                                            anchorEl={this.anchorEl}
                                            anchorOrigin={{ "horizontal": "left", "vertical": "bottom" }}
                                            targetOrigin={{ "horizontal": "left", "vertical": "top" }}
                                            onRequestClose={() => this.setState({ openWidgetBgColorPicker: false })} >
                                            <SketchPicker color={conf.WidgetBackgroundColor} onChange={(color) => {
                                                this.setState((s) => { s.configuration.WidgetBackgroundColor = "rgba(" + color.rgb.r + "," + color.rgb.g + "," + color.rgb.b + "," + color.rgb.a + ")"; return s; });
                                            }} />
                                        </Popover>
                                    </div> : null} />,
                        // ------------------------------------
                        <ListItem
                            key='no-widget-background-color'
                            primaryText="Transparent widgets"
                            style={colorStyle}
                            rightToggle={
                                <Toggle
                                    toggled={conf.WidgetNoBackground}
                                    onToggle={(e: React.MouseEvent<{}>, isInputChecked: boolean) => {
                                        this.setState(s => {
                                            s.configuration.WidgetNoBackground = isInputChecked;
                                            return s;
                                        });
                                    }} />} />,
                        // ------------------------------------
                        <ListItem
                            key='z-depth'
                            primaryText={"zDepth  [" + conf.ZDepth + "]"}
                            style={colorStyle}
                            primaryTogglesNestedList={true}
                            leftCheckbox={
                                <Checkbox
                                    checked={conf.OverrideZDepth}
                                    onCheck={(event, checked) => {
                                        this.setState((s: DashboardConfigurationViewState) => {
                                            s.configuration.OverrideZDepth = checked;
                                            return s;
                                        });
                                    }}
                                />}
                            nestedItems={
                                (conf.OverrideZDepth) ? [
                                    <Slider
                                        key={"zDepth-slider"}
                                        style={{ margin: '0 16px 0 34px' }}
                                        sliderStyle={{ margin: 0 }}
                                        value={conf.ZDepth}
                                        min={0}
                                        max={5}
                                        step={1}
                                        onChange={this.onZDepthChange}
                                    />] : undefined
                            } />,
                        // ------------------------------------
                        <ListItem
                            key='b-radius'
                            primaryText={"Border radius [" + conf.BorderRadius + "px]"}
                            style={colorStyle}
                            primaryTogglesNestedList={true}
                            leftCheckbox={
                                <Checkbox
                                    checked={conf.OverrideBorderRadius}
                                    onCheck={(event, checked) => {
                                        this.setState((s: DashboardConfigurationViewState) => {
                                            s.configuration.OverrideBorderRadius = checked;
                                            return s;
                                        });
                                    }}
                                />}
                            nestedItems={
                                (conf.OverrideBorderRadius) ? [
                                    <Slider
                                        key={"boarder-radius-slider"}
                                        style={{ margin: '0 16px 0 34px' }}
                                        sliderStyle={{ margin: 0 }}
                                        value={conf.BorderRadius}
                                        min={0}
                                        max={20}
                                        step={1}
                                        onChange={this.onBorderRadiusChange}
                                    />] : undefined
                            } />,
                        // ------------------------------------
                        <ListItem
                            primaryTogglesNestedList
                            key='outline-thickness'
                            primaryText={'Outline thickness [' + outline.Thickness + 'px]'}
                            style={colorStyle}
                            nestedItems={[
                                <Slider
                                    key='outline-thickness-slider'
                                    style={{ margin: '0 16px 0 34px' }}
                                    sliderStyle={{ margin: 0 }}
                                    value={outline.Thickness}
                                    min={0}
                                    max={8}
                                    step={1}
                                    onChange={this.onOutlineThicknessChange}
                                />]} />,
                        // ------------------------------------
                        <ListItem
                            key='outline-color'
                            primaryText='Outline color'
                            style={colorStyle}
                            rightToggle={
                                <div>
                                    <div
                                        style={{
                                            display: 'inline-block',
                                            padding: '5px',
                                            cursor: 'pointer',
                                            background: '#fff',
                                            borderRadius: '1px',
                                            boxShadow: '0 0 0 1px rgba(0, 0, 0, .1)'
                                        }}
                                        ref={r => this.outlineColorEl = r}
                                        onClick={() => this.setState({
                                            openOutlineColorPicker: !this.state.openOutlineColorPicker
                                        })}>
                                        <div style={{
                                            width: '36px',
                                            height: '14px',
                                            borderRadius: '2px',
                                            background: outline.Color
                                        }} />
                                    </div>
                                    <Popover
                                        open={this.state.openOutlineColorPicker}
                                        anchorEl={this.outlineColorEl}
                                        anchorOrigin={{
                                            'horizontal': 'left',
                                            'vertical': 'bottom'
                                        }}
                                        targetOrigin={{
                                            'horizontal': 'left',
                                            'vertical': 'top'
                                        }}
                                        onRequestClose={() => this.setState({ openOutlineColorPicker: false })}>
                                        <SketchPicker
                                            color={outline.Color}
                                            onChange={(color) =>
                                                this.setState(s => {
                                                    s.configuration.Outline.Color = 'rgba('
                                                        + color.rgb.r + ','
                                                        + color.rgb.g + ','
                                                        + color.rgb.b + ','
                                                        + color.rgb.a + ')';
                                                    return s;
                                                })} />
                                    </Popover>
                                </div>} />
                        // ------------------------------------
                    ]} />
                <ListItem
                    key='cyan'
                    primaryText="Cyan"
                    primaryTogglesNestedList={true}
                    style={colorStyle}
                    nestedItems={[
                        // ------------------------------------
                        <ListItem
                            key='polling-section'
                            primaryText={"Polling [" +
                                conf.Polling.Enabled + " - " +
                                conf.Polling.Period +
                                ((conf.Polling.PeriodScale == PollingScale.Hours) ? " hours" : (conf.Polling.PeriodScale == PollingScale.Min) ? " min" : " sec") +
                                "]"}
                            style={colorStyle}
                            primaryTogglesNestedList={true}
                            nestedItems={[
                                <ListItem
                                    key='polling'
                                    primaryText="Enable polling"
                                    style={colorStyle}
                                    rightToggle={
                                        <Toggle
                                            toggled={conf.Polling.Enabled}
                                            onToggle={(e: React.MouseEvent<{}>, isInputChecked: boolean) => {
                                                this.setState(s => {
                                                    s.configuration.Polling.Enabled = isInputChecked;
                                                    return s;
                                                });
                                            }} />} />,
                                <ListItem
                                    key='enable-polling'
                                    primaryText="Enabled only when in focus"
                                    style={colorStyle}
                                    rightToggle={
                                        <Toggle
                                            toggled={conf.Polling.EnabledOnlyInFocus}
                                            onToggle={(e: React.MouseEvent<{}>, isInputChecked: boolean) => {
                                                this.setState(s => {
                                                    s.configuration.Polling.EnabledOnlyInFocus = isInputChecked;
                                                    return s;
                                                });
                                            }} />} />,
                                <ListItem
                                    key='send-in-refocus-polling'
                                    primaryText="Send after refocus"
                                    secondaryText="Sends poll request when browser's tab gets focus"
                                    style={colorStyle}
                                    rightToggle={
                                        <Toggle
                                            toggled={conf.Polling.SendPollInRefocus}
                                            onToggle={(e: React.MouseEvent<{}>, isInputChecked: boolean) => {
                                                this.setState(s => {
                                                    s.configuration.Polling.SendPollInRefocus = isInputChecked;
                                                    return s;
                                                });
                                            }} />} />,
                                <ListItem
                                    key={'polling-period-scale'}
                                    primaryText={'Scale'}
                                    style={colorStyle}
                                    rightIconButton={
                                        <DropDownMenu
                                            value={conf.Polling.PeriodScale}
                                            onChange={(e, i, v) => {
                                                this.setState(s => {
                                                    s.configuration.Polling.PeriodScale = v;
                                                    return s;
                                                });
                                            }} >
                                            <MenuItem key={PollingScale.Hours} value={PollingScale.Hours} primaryText="Hours" />
                                            <MenuItem key={PollingScale.Min} value={PollingScale.Min} primaryText="Minutes" />
                                            <MenuItem key={PollingScale.Sec} value={PollingScale.Sec} primaryText="Seconds" />
                                        </DropDownMenu>} />,
                                <Slider
                                    key={"polling-period-timer"}
                                    style={{ margin: '0 28px 0 52px' }}
                                    sliderStyle={{ margin: 0 }}
                                    value={conf.Polling.Period}
                                    min={1}
                                    max={120}
                                    step={1}
                                    onChange={(event, value) => this.setState(s => {
                                        s.configuration.Polling.Period = value;
                                        return s;
                                    })} />
                            ]} />,
                        // ------------------------------------
                        <ListItem
                            primaryText="Session based port events"
                            secondaryText="Sends the data state for all widgets of the page with each event"
                            style={colorStyle}
                            rightToggle={
                                <Toggle
                                    toggled={conf.SessionBasedPortData}
                                    onToggle={(e: React.MouseEvent<{}>, isInputChecked: boolean) => {
                                        this.setState(s => {
                                            s.configuration.SessionBasedPortData = isInputChecked;
                                            return s;
                                        });
                                    }} />} />
                    ]} />
            </List>);
    }
    // ------------------------------------------------------------------------------------------------------
    render() {
        if (this.props.isDrawer)
            return (
                <Drawer
                    key="DashboardConfigurationView"
                    width={this.props.drawerWidth}
                    openSecondary={true}
                    open={this.props.isOpen}
                    docked={false}
                    onRequestChange={this.onConfigurationClose}>
                    {this.renderBody()}
                </Drawer>
            );
        else {
            return (
                <ExDialog
                    Enable={this.props.isOpen}
                    Conf={{
                        title: "Page Settings",
                        actions: [
                            <IconButton tooltip="Cancel" iconClassName="fas fa-times-circle" touch={true} iconStyle={{ color: "white" }}
                                onClick={this.onConfigurationCloseWithoutSave}
                            />,
                            <IconButton tooltip="Save" tooltipPosition='bottom-left' iconClassName="fas fa-upload" touch={true} iconStyle={{ color: "white" }}
                                onClick={this.onConfigurationClose}
                            />,
                        ],
                        bodyFunc: () => this.renderBody(),
                        handleClose: this.onConfigurationCloseWithoutSave
                    }}
                >
                </ExDialog >
            );
        }
    }
    // ------------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: DashboardConfigurationViewProps) {
        if (!_.isEqual(this.props.configuration, prevProps.configuration)) {
            this.setState({
                configuration: _.cloneDeep(this.props.configuration),
                processingImage: false,
                imageNeedsResize: false,
                mobileImageNeedsResize: false,
                errorMessage: ""
            });
        }
    }
    // ------------------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------------